import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Download, FileText, Loader, Calendar, ArrowLeft, Layout, MessageSquare, ChevronDown, ChevronUp } from 'react-feather';
import { Navbar } from './Navbar';
import AIProviderToggle from './AIProviderToggle';
import TemplateSelector from './TemplateSelector';
import ResumeChat from './ResumeChat';
import ClassicTemplate from './templates/ClassicTemplate';
import ModernTemplate from './templates/ModernTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import ExecutiveTemplate from './templates/ExecutiveTemplate';

const api = process.env.REACT_APP_API_URL;

const TEMPLATE_COMPONENTS = {
  modern:    ModernTemplate,
  classic:   ClassicTemplate,
  minimal:   MinimalTemplate,
  executive: ExecutiveTemplate,
};

export default function ATSResume() {
  const navigate = useNavigate();

  const [resume,      setResume]      = useState(null);
  const [dropped,     setDropped]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [downloading, setDownloading] = useState('');
  const [template,    setTemplate]    = useState(() => localStorage.getItem('resumeTemplate') || 'modern');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showChat,    setShowChat]    = useState(false);

  // Generate / load resume on mount
  useEffect(() => {
    axios
      .post(`${api}generate-ats-resume/`, {}, { withCredentials: true })
      .then(res => {
        setResume(res.data.resume);
        setDropped(Array.isArray(res.data.dropped_keywords) ? res.data.dropped_keywords : []);
        const steps = JSON.parse(localStorage.getItem('completedSteps') || '[]');
        if (!steps.includes('atsResume')) {
          steps.push('atsResume');
          localStorage.setItem('completedSteps', JSON.stringify(steps));
        }
      })
      .catch(() => toast.error('Failed to generate ATS resume. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // Persist template choice
  const handleTemplateChange = useCallback(id => {
    setTemplate(id);
    localStorage.setItem('resumeTemplate', id);
  }, []);

  // Called by ResumeChat when Claude returns an updated resume
  const handleResumeUpdate = useCallback(updatedResume => {
    setResume(updatedResume);
    toast.success('Resume updated by Claude!');
  }, []);

  const handleDownload = async format => {
    setDownloading(format);
    try {
      const response = await axios.get(
        `${api}download-resume/?file_format=${format}&template=${template}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const contentType = format === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `ATS_Resume_${template}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to download ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading('');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Loader className="animate-spin text-blue-600 w-12 h-12" />
        <p className="text-gray-600 text-lg font-medium">Building your ATS-optimized resume…</p>
        <p className="text-gray-400 text-sm">This may take up to 30 seconds</p>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-gray-600">Something went wrong. Please go back and try again.</p>
        <button onClick={() => navigate('/keyword-review')} className="px-6 py-2 bg-blue-600 text-white rounded-lg">
          Go Back
        </button>
      </div>
    );
  }

  const TemplateComponent = TEMPLATE_COMPONENTS[template] || ModernTemplate;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-4 py-10">
        <div className="max-w-7xl mx-auto">

          {/* Back link */}
          <button
            onClick={() => navigate('/keyword-review')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition"
          >
            <ArrowLeft className="w-4 h-4" /> Edit Keywords
          </button>

          {/* Page header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Your ATS-Optimized Resume</h1>
              <p className="text-gray-500 text-sm mt-0.5">Preview · edit with Claude · choose template · download</p>
            </div>
            {/* AI provider — locked to the engine chosen at analysis time */}
            <AIProviderToggle locked />
          </div>

          {/* ── Template selector (collapsible) ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-4 overflow-hidden">
            <button
              onClick={() => setShowTemplates(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Layout className="w-4 h-4 text-blue-500" />
                Template: <span className="capitalize text-blue-600 ml-1">{template}</span>
              </div>
              {showTemplates ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {showTemplates && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                <TemplateSelector selected={template} onChange={id => { handleTemplateChange(id); setShowTemplates(false); }} />
              </div>
            )}
          </div>

          {/* ── Main two-column layout ── */}
          <div className="flex flex-col lg:flex-row gap-5">

            {/* ── Left: resume preview ── */}
            <div className="flex-1 min-w-0">
              {/* Download buttons */}
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  onClick={() => handleDownload('pdf')}
                  disabled={!!downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-500 disabled:opacity-50 transition text-sm"
                >
                  {downloading === 'pdf' ? <Loader className="animate-spin w-4 h-4" /> : <Download className="w-4 h-4" />}
                  Download PDF
                </button>
                <button
                  onClick={() => handleDownload('docx')}
                  disabled={!!downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-700 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50 transition text-sm"
                >
                  {downloading === 'docx' ? <Loader className="animate-spin w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  Download Word
                </button>
                <button
                  onClick={() => setShowChat(v => !v)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition text-sm ${
                    showChat
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  {showChat ? 'Hide Chat' : 'Edit with Claude'}
                </button>
              </div>

              {/* Honesty notice — keywords we deliberately left out */}
              {dropped.length > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-semibold text-amber-800 mb-1">
                    ✓ Kept honest — {dropped.length} keyword{dropped.length === 1 ? '' : 's'} left out
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    We didn't add these because your resume doesn't yet support them. Claiming them could backfire in an interview. Add real experience for any you genuinely have, then regenerate.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {dropped.map((k, i) => (
                      <span key={i} className="text-xs bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{k}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Template preview */}
              <div className="bg-white shadow-lg rounded-2xl overflow-auto border border-gray-100">
                <TemplateComponent resume={resume} />
              </div>
            </div>

            {/* ── Right: Claude chat ── */}
            {showChat && (
              <div className="w-full lg:w-96 flex-shrink-0">
                <div className="sticky top-24 h-[680px]">
                  <ResumeChat onResumeUpdate={handleResumeUpdate} />
                </div>
              </div>
            )}
          </div>

          {/* ── Book a review CTA ── */}
          <div className="mt-8 bg-orange-50 border border-orange-200 rounded-2xl p-6 text-center">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Want an expert eye on your resume?</h3>
            <p className="text-gray-500 text-sm mb-4">
              Book a 30-minute one-on-one resume review session with one of our specialists.
            </p>
            <button
              onClick={() => navigate('/appointment')}
              className="flex items-center gap-2 mx-auto px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-400 transition"
            >
              <Calendar className="w-5 h-5" />
              Book a Review Session
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
